import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useGlobalContext } from "../../context/global";

const WATCHED_KEYS = ["user_platform", "clientId"];
const AUTH_COOKIE = "_auth";
const PUBLIC_PATHS = ["/login", "/client-details"];

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === "undefined" || raw === "null") return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const hasAuthCookie = () =>
  document.cookie
    .split(";")
    .some((c) => c.trim().startsWith(`${AUTH_COOKIE}=`));

const SessionContextGuard = () => {
  const location = useLocation();
  const { selectedPlatform, clientId } = useGlobalContext();
  const [conflict, setConflict] = useState(null);

  // What this tab's UI is built from. Mirrored into a ref so listeners stay
  // attached for the lifetime of the tab without re-binding on every change.
  const liveRef = useRef({
    selectedPlatform,
    clientId,
    pathname: location.pathname,
  });
  const conflictRef = useRef(null);

  useEffect(() => {
    liveRef.current = {
      selectedPlatform,
      clientId,
      pathname: location.pathname,
    };
  }, [selectedPlatform, clientId, location.pathname]);

  // Never guard auth/public screens — focus/visibility while copying a
  // password must not treat a missing cookie as "signed out in another tab".
  useEffect(() => {
    if (!PUBLIC_PATHS.includes(location.pathname)) return;
    conflictRef.current = null;
    setConflict(null);
  }, [location.pathname]);

  const evaluate = useCallback(() => {
    if (conflictRef.current) return;

    const {
      selectedPlatform: tabPlatform,
      clientId: tabClientId,
      pathname,
    } = liveRef.current;

    if (PUBLIC_PATHS.includes(pathname) || !tabClientId) return;

    const storedClientId = readJson("clientId");
    const storedPlatform = readJson("user_platform") ?? "amazon";

    let next = null;
    if (!storedClientId || !hasAuthCookie()) {
      next = { type: "logout" };
    } else if (
      storedPlatform !== tabPlatform ||
      String(storedClientId) !== String(tabClientId)
    ) {
      next = { type: "stale" };
    }

    if (next) {
      conflictRef.current = next;
      setConflict(next);
    }
  }, []);

  useEffect(() => {
    // A client switch writes localStorage before React commits the new state,
    // so give that window a moment to close before judging the tab stale.
    const evaluateSoon = () => window.setTimeout(evaluate, 250);

    const onStorage = (event) => {
      // A null key means localStorage.clear() — re-check everything.
      if (event.key !== null && !WATCHED_KEYS.includes(event.key)) return;
      evaluate();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") evaluateSoon();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", evaluateSoon);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", evaluateSoon);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [evaluate]);

  if (!conflict) return null;

  const isLogout = conflict.type === "logout";

  return (
    <Dialog
      open
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: "18px", fontWeight: 600 }}>
        {isLogout ? "You have been signed out" : "This tab is out of date"}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "#555" }}>
          {isLogout
            ? "You signed out in another tab. Sign in again to continue."
            : "The account or platform was changed in another tab. Reload this tab to continue with the current selection."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={() =>
            window.location.assign(isLogout ? "/login" : "/")
          }
        >
          {isLogout ? "Go to login" : "Reload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionContextGuard;
